import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

type Gaze = { x: number; y: number };

export function Glasses({ className = '', decorative = true, mood = 'curious' }: { className?: string; decorative?: boolean; mood?: 'curious' | 'cta' | 'down' }) {
  const reduceMotion = useReducedMotion();
  const ref = useRef<SVGSVGElement>(null);
  const [gaze, setGaze] = useState<Gaze>({ x: 0, y: mood === 'down' ? 3 : 1 });

  useEffect(() => {
    const finePointer = typeof window.matchMedia === 'function' ? window.matchMedia('(pointer:fine)') : undefined;
    if (reduceMotion || !finePointer?.matches) return;
    let frame = 0;
    const onMove = (event: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        const x = Math.max(-5, Math.min(5, (event.clientX - (rect.left + rect.width / 2)) / 25));
        const y = Math.max(-4, Math.min(5, (event.clientY - (rect.top + rect.height / 2)) / 25));
        setGaze({ x, y });
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onMove);
    };
  }, [reduceMotion]);

  const target = mood === 'cta' ? { x: 5, y: 2 } : mood === 'down' ? { x: 0, y: 4 } : gaze;
  return (
    <svg ref={ref} className={`glasses ${className}`} viewBox="0 0 220 106" aria-hidden={decorative || undefined} role={decorative ? undefined : 'img'}>
      <g fill="var(--paper)" stroke="var(--purple)" strokeWidth="12">
        <circle cx="61" cy="51" r="44" />
        <circle cx="159" cy="51" r="44" />
      </g>
      <rect x="101" y="44" width="18" height="14" rx="7" fill="var(--stone-3)" />
      <motion.g
        animate={reduceMotion ? { x: 0, y: 1 } : target}
        transition={{ type: 'spring', stiffness: 160, damping: 18 }}
      >
        <motion.ellipse cx="61" cy="56" rx="8" ry="9" fill="var(--ink)" animate={reduceMotion ? undefined : { scaleY: [1, 1, 0.08, 1, 1] }} transition={{ duration: 4.8, repeat: Infinity, times: [0, 0.77, 0.8, 0.84, 1] }} />
        <motion.ellipse cx="159" cy="56" rx="8" ry="9" fill="var(--ink)" animate={reduceMotion ? undefined : { scaleY: [1, 1, 0.08, 1, 1] }} transition={{ duration: 4.8, repeat: Infinity, times: [0, 0.77, 0.8, 0.84, 1] }} />
      </motion.g>
      <path d="M38 39c7-10 15-14 23-14s16 4 23 14M136 39c7-10 15-14 23-14s16 4 23 14" fill="none" stroke="var(--purple-soft)" strokeWidth="7" strokeLinecap="round" />
    </svg>
  );
}

export function Wordmark({ light = false }: { light?: boolean }) {
  return (
    <span className={`wordmark${light ? ' wordmark--light' : ''}`} aria-label="reciboofertas">
      <span aria-hidden="true">recib</span>
      <Glasses className="wordmark__glasses" />
      <span aria-hidden="true">fertas</span>
    </span>
  );
}
